import React, { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Send, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { submitContact } from '@/store/slices/contactSlice';

// same PHP endpoint used by ContactPage
const SENDMAIL_URL = 'https://prayasinstitute.com/sendmail/sendmail.php';

const EnquiryPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    school: '',
    class: '',
    course: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const dispatch = useDispatch();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      !formData.school ||
      !formData.class ||
      !formData.course
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (formData.phone.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('type', 'enquiry');
      payload.append('name', formData.name);
      payload.append('phone', formData.phone);
      payload.append('mail', formData.email); // sendmail.php reads $_POST['mail']
      payload.append('school', formData.school);
      payload.append('class', formData.class);
      payload.append('course', formData.course);
      payload.append('message', formData.message);

      const res = await fetch(SENDMAIL_URL, {
        method: 'POST',
        body: payload,
      });

      const data = await res.json();

      // Backend contact API bhi hit karo (ContactPage ki tarah, sendmail ke response pe depend nahi)
      dispatch(submitContact({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        s_name: formData.school,
        cid: formData.class,
        course_interest: formData.course,
        message: formData.message,
        enquiry_type: 'admission'
      }));

      if (data.success) {
        toast.success('Enquiry submitted successfully!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          school: '',
          class: '',
          course: '',
          message: ''
        });
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      toast.error('Unable to submit enquiry. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const benefits = [
    'Free counseling session',
    'Scholarship opportunities',
    'Demo class access',
    'Study material preview'
  ];

  return (
    <div>
      <PageHeader
        title="Enquiry Form"
        subtitle="Take the first step towards your success. Fill out the form below"
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Enquiry' }
        ]}
      />

      <section className="section-padding bg-background">
        <div className="container-custom max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12">

            {/* FORM */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Card className="border-2">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-heading font-bold mb-6">
                    Submit Your Details
                  </h2>

                  <form onSubmit={handleSubmit} className="space-y-6">

                    {/* Name */}
                    <div>
                      <Label>Full Name *</Label>
                      <Input
                        type="text"
                        placeholder="Enter your full name"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        required
                        minLength={3}
                        className="mt-2"
                      />
                    </div>

                    {/* Email */}
                    <div>
                      <Label>Email Address *</Label>
                      <Input
                        type="email"
                        placeholder="Enter Email"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* Phone */}
                    <div>
                      <Label>Phone Number *</Label>
                      <Input
                        type="tel"
                        inputMode="numeric"
                        placeholder="Enter Phone Number"
                        value={formData.phone}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '');
                          if (value.length <= 10) {
                            handleChange('phone', value);
                          }
                        }}
                        pattern="[0-9]{10}"
                        title="Phone number must be exactly 10 digits"
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* School */}
                    <div>
                      <Label>School Name *</Label>
                      <Input
                        type="text"
                        placeholder="Enter your school name"
                        value={formData.school}
                        onChange={(e) => handleChange('school', e.target.value)}
                        required
                        className="mt-2"
                      />
                    </div>

                    {/* Class Select */}
                    <div>
                      <Label>Current Class *</Label>
                      <Select
                        value={formData.class}
                        onValueChange={(value) => handleChange('class', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select your class" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="6">Class VI</SelectItem>
                          <SelectItem value="7">Class VII</SelectItem>
                          <SelectItem value="8">Class VIII</SelectItem>
                          <SelectItem value="9">Class IX</SelectItem>
                          <SelectItem value="10">Class X</SelectItem>
                          <SelectItem value="11">Class XI</SelectItem>
                          <SelectItem value="12">Class XII</SelectItem>
                          <SelectItem value="12pass">XII Pass (Dropper)</SelectItem>
                        </SelectContent>
                      </Select>

                    </div>

                    {/* Course Select */}
                    <div>
                      <Label>Interested Course *</Label>
                      <Select
                        value={formData.course}
                        onValueChange={(value) => handleChange('course', value)}
                      >
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a course" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="foundation">Foundation (VI-VIII)</SelectItem>
                          <SelectItem value="pre-foundation">Pre-Foundation (IX-X)</SelectItem>
                          <SelectItem value="board-preparation">Board preparation</SelectItem>
                          <SelectItem value="jee">IIT-JEE Program</SelectItem>
                          <SelectItem value="neet">NEET Program</SelectItem>
                          <SelectItem value="dropper">Dropper Batch</SelectItem>
                        </SelectContent>
                      </Select>

                    </div>

                    {/* Message */}
                    <div>
                      <Label>Message (Optional)</Label>
                      <Textarea
                        placeholder="Tell us about your goals..."
                        value={formData.message}
                        onChange={(e) => handleChange('message', e.target.value)}
                        rows={4}
                        className="mt-2"
                      />
                    </div>

                    <Button type="submit" size="lg" disabled={isSubmitting} className="w-full">
                      {isSubmitting ? 'Submitting...' : 'Submit Enquiry'}
                      <Send className="ml-2 h-5 w-5" />
                    </Button>

                  </form>
                </CardContent>
              </Card>
            </motion.div>

            {/* Info Section */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-8"
            >
              <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                    What Happens Next?
                  </h3>
                  <div className="space-y-4">
                    {[
                      'Our counselor will contact you within 24 hours',
                      'Schedule a free counseling session',
                      'Visit our institute for a campus tour',
                      'Attend a free demo class',
                      'Get personalized course recommendations'
                    ].map((step, idx) => (
                      <div key={idx} className="flex items-start space-x-3">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-sm font-bold text-primary">{idx + 1}</span>
                        </div>
                        <p className="text-foreground leading-relaxed">{step}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-secondary/10 to-accent/10 border-secondary/20">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold text-foreground mb-6">
                    Benefits of Enrolling
                  </h3>
                  <div className="space-y-4">
                    {benefits.map((benefit, idx) => (
                      <div key={idx} className="flex items-center space-x-3">
                        <CheckCircle2 className="h-6 w-6 text-success flex-shrink-0" />
                        <span className="text-foreground">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-primary text-primary-foreground">
                <CardContent className="p-8">
                  <h3 className="text-xl font-heading font-bold mb-4">
                    Need Immediate Assistance?
                  </h3>
                  <p className="text-primary-foreground/90 mb-6">
                    Call us directly for instant support
                  </p>
                  <a href="tel:+918839354369">
                    <Button
                      size="lg"
                      className="w-full bg-secondary hover:bg-secondary-dark text-secondary-foreground"
                    >
                      Call +91 88393 54369
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default EnquiryPage;